import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateMetricsOnFilteredData } from '../../../../../client-customizations/default/metrics';
import { IMetricsDataModel } from '../../../../../data-models/metrics.data-model';
import { ViewModel } from '../../../../../models/view.model';
import { companyMapAtom } from '../../../../../services/hooks/useSaveCompanyData';
import { formulaFieldsListLoadable } from '../../../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { formulaFieldsForEntity } from '../../../../../util/formula-fields';
import { fundsByIdMapAtom } from '../../../../../services/state/AppConfigStateJ';
import { dateToPlainDateString, TemporalPlainDateFromJsDate } from '../../../../../util/date-utilts';
import { useSelectedCurrency } from '../../../../../hooks/useSelectedCurrency';
import { useEntityManagementEnabled } from '../../../../EntityManagement/hooks/useEntityManagementEnabled';
import { allInvestmentRoundsMapStateJ } from '../../../../Finance2/state/FinanceStateJ';
import { useColumns } from '../../../hooks/useColumns/useColumns';
import { useCurrentViewMetrics } from '../../../hooks/useCurrentViewMetrics';
import { useInitializeMetricsData } from '../../../hooks/useInitializeMetricsData';
import { useScrollToSelectedCompany } from '../../../hooks/useScrollToSelectedCompany';
import { useUpdateSelectedView } from '../../../hooks/useViewActions';
import { filterMetricsTransactionsAndFundDataByFund } from '../../../services/kpi/KpiCalculator';
import { familyMetricsResponseByDateAtom, metricsFromFilteredDataAtom } from '../../../state/MetricsStateJ';
import { selectedViewDatePFJ } from '../../../state/PageStateJ';
import { rowGroupColumnsState } from '../../../state/UIStateJ';
import {
  CalculatedMetricsDataModel,
  MetricsGroupColumns,
  runMetricsCalculations,
} from '../MetricsCalculator';
import { IPortfolioTreeRow } from '../../../tree/computeTreeRows';
import { IPortfolioTreeSetting, PortfolioTreeToggle } from '../../../tree/PortfolioTreeToggle';
import { treeAutoGroupColumnDef } from '../../../tree/treeAutoGroupColumn';
import { indexMetricsByCompanyId } from '../../../tree/publishedRowsFromTree';
import { repoolTreeRows } from '../../../tree/repoolTreeRows';
import { usePortfolioTree } from '../../../tree/usePortfolioTree';
import { createStableFilteredMetricsCalculator } from '../stableFilteredMetricsCalculator';
import { SharedTable } from './SharedTable';

const FLAT_TREE_SETTING: IPortfolioTreeSetting = { enabled: false, mode: 'rollup' };

export const OverviewTable: React.FC<{ view: ViewModel }> = function OverviewTable({ view }) {
  const { ready } = useInitializeMetricsData();
  const [rowGroupColumns, setRowGroupColumns] = useAtom(rowGroupColumnsState);
  const columnsMeta = useColumns();
  const date = useAtomValue(selectedViewDatePFJ);
  const { selectedCurrency } = useSelectedCurrency();
  const { metrics, filteredMetrics } = useCurrentViewMetrics(view);
  const fundsMap = useAtomValue(fundsByIdMapAtom);
  const setMetricsFromFilteredData = useSetAtom(metricsFromFilteredDataAtom);
  const investmentMap = useAtomValue(allInvestmentRoundsMapStateJ);
  // Only actually fetches (calculateFundTypeData: true) while rowGroupColumns is grouped by
  // Fund Family — see familyMetricsResponseByDateAtom's own `enabled` gate.
  const familyMetricsResponse = useAtomValue(
    familyMetricsResponseByDateAtom({ date: TemporalPlainDateFromJsDate(date), currency: selectedCurrency })
  );
  const familyMetrics = useMemo(
    () => familyMetricsResponse.data?.metrics ?? [],
    [familyMetricsResponse.data]
  );

  // Use the custom hook for scrolling to selected company
  useScrollToSelectedCompany();
  const companyMap = useAtomValue(companyMapAtom);
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  // Memoized so the fallback [] keeps its identity — a fresh array per render
  // would re-trigger the groupedMetrics memo below on every render. Only
  // company formulas evaluate against metrics rows.
  const formulaFields = useMemo(
    () => formulaFieldsForEntity(formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [], 'company'),
    [formulaLoad]
  );

  // The grid publishes its visible rows back into filteredMetrics, so this memo re-runs on the
  // grid's own output. The base-row pipeline must not read filteredMetrics: rebuilding these
  // arrays on a publish echo re-ingests identical rows and loops the grid forever (full redraws —
  // context menu closes, logo cells remount).
  // The tree does its own aggregation and must be built from rows the fund filter has already
  // narrowed, or its parents would count funds the reader deselected.
  const fundScopedMetrics = useMemo(() => {
    // No rows until the date's metrics have arrived; an empty set here would publish as the date's rows.
    if (!ready || metrics === undefined) {
      return undefined;
    }
    const newData = metrics as CalculatedMetricsDataModel[];
    if (view.filteredFunds == null) {
      return newData;
    }
    const selectedFunds = view.filteredFunds.reduce((res, fund) => {
      return res.add(fund.id);
    }, new Set<number>());
    return filterMetricsTransactionsAndFundDataByFund(
      newData,
      selectedFunds,
      fundsMap
    ) as CalculatedMetricsDataModel[];
  }, [ready, metrics, view.filteredFunds, fundsMap]);

  const groupedMetrics = useMemo(() => {
    if (!ready || !fundScopedMetrics) {
      return undefined;
    }
    const hasFundFilter = view.filteredFunds != null;
    const customFundFields = columnsMeta.filter((col) => {
      return col.path.startsWith('fundCompany');
    });

    let newData = fundScopedMetrics;

    if (
      hasFundFilter ||
      customFundFields.length > 0 ||
      rowGroupColumns.some((column) => MetricsGroupColumns[column.colId])
    ) {
      newData = runMetricsCalculations(
        fundsMap,
        newData,
        rowGroupColumns,
        date,
        customFundFields,
        companyMap,
        investmentMap,
        formulaFields,
        familyMetrics
      );
    }

    return newData;
  }, [
    ready,
    fundScopedMetrics,
    view.filteredFunds,
    columnsMeta,
    rowGroupColumns,
    date,
    fundsMap,
    companyMap,
    investmentMap,
    formulaFields,
    familyMetrics,
  ]);

  // Tenant calculators may clone rows every call; the stable wrapper returns the previous result
  // for an echoed filtered set so rowData identity settles (see stableFilteredMetricsCalculator).
  const [stableCalculateOnFilteredData] = useState(() =>
    createStableFilteredMetricsCalculator(calculateMetricsOnFilteredData)
  );

  const metricsBasedOnGroupings = useMemo(() => {
    if (ready && groupedMetrics) {
      const res = stableCalculateOnFilteredData(
        groupedMetrics,
        (filteredMetrics as IMetricsDataModel[]) ?? [],
        TemporalPlainDateFromJsDate(date)
      );

      // the fund scoping and date these rows were built under, published with them so readers can't mix bases
      return {
        ...res,
        fundBasis: view.filteredFunds?.map((fund) => fund.id) ?? null,
        asOfDate: dateToPlainDateString(date),
      };
    }
    // not fund-scoped
    return { metrics, filteredMetrics, fundBasis: null, asOfDate: dateToPlainDateString(date) };
  }, [
    ready,
    groupedMetrics,
    stableCalculateOnFilteredData,
    metrics,
    filteredMetrics,
    view.filteredFunds,
    date,
  ]);

  useEffect(() => {
    setMetricsFromFilteredData(
      (metricsBasedOnGroupings?.filteredMetrics as CalculatedMetricsDataModel[]) ?? []
    );
  }, [metricsBasedOnGroupings, setMetricsFromFilteredData]);

  const entityManagementEnabled = useEntityManagementEnabled();
  const treeSetting = view.portfolioTree ?? FLAT_TREE_SETTING;
  const treeRequested = entityManagementEnabled && treeSetting.enabled;
  const {
    rows: treeRows,
    leafKeysByNode,
    isLoading: treeLoading,
    isUnavailable,
  } = usePortfolioTree(fundScopedMetrics as IMetricsDataModel[] | undefined, treeSetting.mode, treeRequested);

  // The leaf keys passing the grid's own filters, reported back by SharedTable. Identity-stable
  // on echoes: repooled rowData re-triggers the grid's publish, and a fresh Set every time would
  // rebuild rowData forever.
  const [visibleLeafKeys, setVisibleLeafKeys] = useState<ReadonlySet<string> | undefined>(undefined);
  const onVisibleTreeLeavesChange = useCallback((keys: ReadonlySet<string>) => {
    setVisibleLeafKeys((prev) =>
      prev != null && prev.size === keys.size && [...keys].every((key) => prev.has(key)) ? prev : keys
    );
  }, []);
  // A set carried across views, mode switches or tree rebuilds could narrow rows it
  // never came from — leaf keys differ between roll-up and individual mode.
  useEffect(() => {
    setVisibleLeafKeys(undefined);
  }, [view.id, treeRequested, treeSetting.mode]);

  // Metrics hydrate separately from the structure queries, so no rows yet means "no tree" only
  // once nothing is still arriving.
  const treePending =
    treeRequested && (treeLoading || metrics === undefined || fundScopedMetrics === undefined);
  const treeUnavailable = treeRequested && (isUnavailable || (!treePending && treeRows === undefined));
  const treeActive = treeRequested && !treeUnavailable;

  // Roll-up and Individual differ only on a company held through a vehicle: no vehicle row,
  // no row the two modes read differently, so the pair would do nothing. Unknown while the
  // rows are still arriving.
  const modeInert =
    treeActive &&
    treeRows != null &&
    !treeRows.some((row) => (row as IPortfolioTreeRow).treeKind === 'vehicle');

  // Indexed off the rows flat mode publishes: the KPI atoms must receive literally the same
  // objects in both modes.
  const treeMetricsByCompanyId = useMemo(
    () =>
      treeActive
        ? indexMetricsByCompanyId(metricsBasedOnGroupings?.metrics as IMetricsDataModel[] | undefined)
        : undefined,
    [treeActive, metricsBasedOnGroupings]
  );

  // Ancestor rows re-pooled over what the filters kept (FD-4988); untouched rows when
  // everything is visible, so the no-filter path is byte-identical to the build.
  const displayedTreeRows = useMemo(() => {
    if (!treeActive || !treeRows || !leafKeysByNode || !visibleLeafKeys) {
      return treeRows;
    }
    return repoolTreeRows(treeRows, leafKeysByNode, visibleLeafKeys, date ?? new Date());
  }, [treeActive, treeRows, leafKeysByNode, visibleLeafKeys, date]);

  const updateSelectedView = useUpdateSelectedView();
  const onTreeChange = useCallback(
    (next: IPortfolioTreeSetting) => {
      updateSelectedView({ portfolioTree: next });
    },
    [updateSelectedView]
  );

  return (
    <SharedTable
      key={`${view.id}-${treeActive ? 'tree' : 'flat'}`}
      view={view}
      rowData={
        treeActive
          ? (displayedTreeRows as unknown as IMetricsDataModel[] | undefined)
          : (metricsBasedOnGroupings?.metrics as IMetricsDataModel[] | undefined)
      }
      fundBasis={metricsBasedOnGroupings?.fundBasis ?? null}
      asOfDate={metricsBasedOnGroupings?.asOfDate}
      setRowGroupColumns={setRowGroupColumns}
      loading={treeActive ? treePending : undefined}
      treeData={treeActive}
      getDataPath={getTreeDataPath}
      onVisibleTreeLeavesChange={treeActive ? onVisibleTreeLeavesChange : undefined}
      treeAutoGroupColumnDef={treeAutoGroupColumnDef}
      treeMetricsByCompanyId={treeMetricsByCompanyId}
      headingControls={
        entityManagementEnabled ? (
          <PortfolioTreeToggle
            value={treeSetting}
            onChange={onTreeChange}
            disabled={treeUnavailable}
            modeInert={modeInert}
          />
        ) : null
      }
    />
  );
};

function getTreeDataPath(row: IMetricsDataModel): string[] {
  return (row as unknown as IPortfolioTreeRow).treePath;
}
